import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiEdit2,
  FiInfo,
  FiLock,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const emptyPolicy = {
  code: "",
  name: "",
  description: "",
  countPaidLeaveAsQualifying: false,
  isActive: true,
  tiers: [{ minimumQualifyingDays: "", holidayDays: "" }],
};

const toNumber = (value) => (value === "" ? NaN : Number(value));

function normalisePolicy(policy) {
  return {
    code: policy.code || "",
    name: policy.name || "",
    description: policy.description || "",
    countPaidLeaveAsQualifying: Boolean(policy.countPaidLeaveAsQualifying),
    isActive: policy.isActive !== false,
    tiers: (policy.tiers || [])
      .slice()
      .sort(
        (left, right) =>
          Number(left.minimumQualifyingDays) -
          Number(right.minimumQualifyingDays),
      )
      .map((tier) => ({
        minimumQualifyingDays: String(tier.minimumQualifyingDays ?? ""),
        holidayDays: String(tier.holidayDays ?? ""),
      })),
  };
}

export default function AttendanceHolidayPoliciesPage() {
  const { accessToken } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(emptyPolicy);

  const loadPolicies = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setMessage("");
      const response = await apiRequest("/attendance-holiday-policies", {
        accessToken,
      });
      setPolicies(response || []);
    } catch (error) {
      setMessage(
        error.message || "Unable to load attendance and holiday policies.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const visiblePolicies = useMemo(
    () => policies.filter((policy) => showInactive || policy.isActive),
    [policies, showInactive],
  );

  const openCreate = () => {
    setEditingPolicy(null);
    setForm({
      ...emptyPolicy,
      tiers: [{ minimumQualifyingDays: "", holidayDays: "" }],
    });
    setIsModalOpen(true);
    setMessage("");
  };

  const openEdit = (policy) => {
    setEditingPolicy(policy);
    setForm(normalisePolicy(policy));
    setIsModalOpen(true);
    setMessage("");
  };

  const closeModal = () => {
    if (isSaving) return;
    setEditingPolicy(null);
    setForm(emptyPolicy);
    setIsModalOpen(false);
  };

  const updateField = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateTier = (index, field, value) => {
    setForm((current) => ({
      ...current,
      tiers: current.tiers.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, [field]: value } : tier,
      ),
    }));
  };

  const addTier = () => {
    setForm((current) => ({
      ...current,
      tiers: [...current.tiers, { minimumQualifyingDays: "", holidayDays: "" }],
    }));
  };

  const removeTier = (index) => {
    setForm((current) => ({
      ...current,
      tiers: current.tiers.filter((_, tierIndex) => tierIndex !== index),
    }));
  };

  const savePolicy = async (event) => {
    event.preventDefault();
    setMessage("");

    const code = form.code.trim().toUpperCase();
    const name = form.name.trim();
    const tiers = form.tiers
      .map((tier, index) => ({
        minimumQualifyingDays: toNumber(tier.minimumQualifyingDays),
        holidayDays: toNumber(tier.holidayDays),
        sortOrder: index + 1,
      }))
      .sort(
        (left, right) =>
          left.minimumQualifyingDays - right.minimumQualifyingDays,
      );

    if (!code || !name) {
      setMessage("Policy code and policy name are required.");
      return;
    }

    if (
      !tiers.length ||
      tiers.some(
        (tier) =>
          !Number.isFinite(tier.minimumQualifyingDays) ||
          !Number.isFinite(tier.holidayDays) ||
          tier.minimumQualifyingDays <= 0 ||
          tier.holidayDays <= 0,
      )
    ) {
      setMessage(
        "Every tier needs qualifying days and paid holiday days greater than zero.",
      );
      return;
    }

    if (
      tiers.some(
        (tier, index) =>
          index > 0 &&
          tier.minimumQualifyingDays <= tiers[index - 1].minimumQualifyingDays,
      )
    ) {
      setMessage(
        "Each tier must use a higher qualifying-days threshold than the one before it.",
      );
      return;
    }

    const payload = {
      code,
      name,
      description: form.description.trim() || undefined,
      countPaidLeaveAsQualifying: form.countPaidLeaveAsQualifying,
      isActive: form.isActive,
      tiers,
    };

    try {
      setIsSaving(true);
      if (editingPolicy) {
        await apiRequest(`/attendance-holiday-policies/${editingPolicy.id}`, {
          method: "PATCH",
          body: payload,
          accessToken,
        });
      } else {
        await apiRequest("/attendance-holiday-policies", {
          method: "POST",
          body: payload,
          accessToken,
        });
      }

      closeModal();
      await loadPolicies();
    } catch (error) {
      const backendMessage = Array.isArray(error.details?.message)
        ? error.details.message.join(", ")
        : error.message;
      setMessage(backendMessage || "Unable to save this policy.");
    } finally {
      setIsSaving(false);
    }
  };

  const deactivatePolicy = async (policy) => {
    if (
      !window.confirm(
        `Deactivate “${policy.name}”? Existing assignments stay intact, but it cannot be selected for new assignments.`,
      )
    )
      return;

    try {
      setMessage("");
      await apiRequest(`/attendance-holiday-policies/${policy.id}/deactivate`, {
        method: "POST",
        accessToken,
      });
      await loadPolicies();
    } catch (error) {
      setMessage(error.message || "Unable to deactivate this policy.");
    }
  };

  return (
    <div className="pb-12">
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-700">
            HRMS configuration
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
            Leave & Holiday Policies
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
            Set the attendance thresholds that progressively unlock paid holiday
            allowance. The rule applies consistently to monthly and off-cycle
            payroll.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
              className="h-4 w-4 accent-primary-700"
            />{" "}
            Show inactive
          </label>
          <button
            type="button"
            onClick={loadPolicies}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary-800"
          >
            <FiPlus /> Create policy
          </button>
        </div>
      </div>

      {message ? (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <FiAlertCircle className="mt-0.5 shrink-0" /> <span>{message}</span>
        </div>
      ) : null}

      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <InfoCard
          title="How it works"
          text="Present and late count as 1 qualifying day; half days count as 0.5."
        />
        <InfoCard
          title="Progressive entitlement"
          text="Each threshold unlocks only the stated number of paid holiday days for that month."
        />
        <InfoCard
          title="Safe history"
          text="Employee assignments are effective-dated, so a future policy change does not alter old payroll."
        />
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-100 bg-white py-20 text-center text-gray-500">
          Loading policies…
        </div>
      ) : visiblePolicies.length ? (
        <div className="grid gap-5 xl:grid-cols-2">
          {visiblePolicies.map((policy) => (
            <PolicyCard
              key={policy.id}
              policy={policy}
              onEdit={() => openEdit(policy)}
              onDeactivate={() => deactivatePolicy(policy)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
          <FiInfo className="mx-auto text-3xl text-primary-600" />
          <h2 className="mt-4 text-lg font-semibold text-gray-900">
            No policies yet
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-500">
            Create the first policy, for example: 6 qualifying days unlock 1
            paid holiday and 22 qualifying days unlock 4.
          </p>
          <button
            type="button"
            onClick={openCreate}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary-700 px-4 py-3 text-sm font-semibold text-white"
          >
            <FiPlus /> Create first policy
          </button>
        </div>
      )}

      {isModalOpen ? (
        <PolicyModal
          editingPolicy={editingPolicy}
          form={form}
          isSaving={isSaving}
          onClose={closeModal}
          onChange={updateField}
          onTierChange={updateTier}
          onAddTier={addTier}
          onRemoveTier={removeTier}
          onSubmit={savePolicy}
        />
      ) : null}
    </div>
  );
}

function InfoCard({ title, text }) {
  return (
    <div className="rounded-2xl border border-primary-100 bg-primary-50/35 p-5">
      <p className="text-sm font-semibold text-primary-900">{title}</p>
      <p className="mt-2 text-sm leading-6 text-gray-600">{text}</p>
    </div>
  );
}

function PolicyCard({ policy, onEdit, onDeactivate }) {
  const tiers = [...(policy.tiers || [])].sort(
    (a, b) => Number(a.minimumQualifyingDays) - Number(b.minimumQualifyingDays),
  );
  return (
    <article
      className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${policy.isActive ? "border-gray-100" : "border-gray-200 opacity-75"}`}
    >
      <div className="flex flex-col gap-4 border-b border-gray-100 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">
              {policy.name}
            </h2>
            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${policy.isActive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}
            >
              {policy.isActive ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="mt-1 font-mono text-xs font-semibold tracking-wide text-primary-700">
            {policy.code}
          </p>
          {policy.description ? (
            <p className="mt-3 text-sm leading-6 text-gray-500">
              {policy.description}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <FiEdit2 /> Edit
          </button>
          {policy.isActive ? (
            <button
              type="button"
              onClick={onDeactivate}
              className="inline-flex items-center gap-2 rounded-lg border border-red-100 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
            >
              <FiLock /> Deactivate
            </button>
          ) : null}
        </div>
      </div>
      <div className="px-6 py-5">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400">
          Progressive thresholds
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {tiers.map((tier) => (
            <span
              key={
                tier.id || `${tier.minimumQualifyingDays}-${tier.holidayDays}`
              }
              className="rounded-lg bg-primary-50 px-3 py-2 text-xs font-semibold text-primary-800"
            >
              {tier.minimumQualifyingDays} qualifying days → {tier.holidayDays}{" "}
              paid holiday{Number(tier.holidayDays) === 1 ? "" : "s"}
            </span>
          ))}
        </div>
        <p className="mt-4 text-xs leading-5 text-gray-500">
          Approved paid leave{" "}
          {policy.countPaidLeaveAsQualifying ? (
            <strong className="text-emerald-700">
              counts as qualifying attendance
            </strong>
          ) : (
            <strong className="text-gray-700">
              does not count as qualifying attendance
            </strong>
          )}
          .
        </p>
      </div>
    </article>
  );
}

function PolicyModal({
  editingPolicy,
  form,
  isSaving,
  onClose,
  onChange,
  onTierChange,
  onAddTier,
  onRemoveTier,
  onSubmit,
}) {
  const inputClass =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100";
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 p-4 sm:p-8">
      <div className="mx-auto my-4 max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5 sm:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-700">
              HRMS configuration
            </p>
            <h2 className="mt-1 text-xl font-bold text-gray-900">
              {editingPolicy
                ? "Edit Leave & Holiday Policy"
                : "Create Leave & Holiday Policy"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100"
          >
            <FiX size={20} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-7 px-6 py-6 sm:px-8">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <ModalField
              label="Policy code"
              hint="Uppercase internal identifier, e.g. STANDARD_4"
            >
              <input
                name="code"
                value={form.code}
                onChange={onChange}
                maxLength="50"
                placeholder="STANDARD_4"
                className={inputClass}
              />
            </ModalField>
            <ModalField label="Policy name">
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                maxLength="150"
                placeholder="Standard monthly entitlement"
                className={inputClass}
              />
            </ModalField>
          </div>
          <ModalField label="Description">
            <textarea
              name="description"
              value={form.description}
              onChange={onChange}
              maxLength="500"
              rows="3"
              placeholder="Explain who should receive this policy."
              className={`${inputClass} resize-y`}
            />
          </ModalField>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
            <input
              type="checkbox"
              name="countPaidLeaveAsQualifying"
              checked={form.countPaidLeaveAsQualifying}
              onChange={onChange}
              className="mt-1 h-4 w-4 accent-primary-700"
            />
            <span>
              <span className="block text-sm font-semibold text-gray-800">
                Count approved paid leave as qualifying attendance
              </span>
              <span className="mt-1 block text-xs leading-5 text-gray-500">
                When enabled, a paid-leave day also helps the employee unlock
                the next holiday tier.
              </span>
            </span>
          </label>
          <div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">
                  Progressive entitlement tiers
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Set the maximum paid holiday unlocked after each
                  qualifying-days threshold.
                </p>
              </div>
              <button
                type="button"
                onClick={onAddTier}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-primary-200 px-3 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50"
              >
                <FiPlus /> Add tier
              </button>
            </div>
            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-500">
                <span>Qualifying days</span>
                <span>Paid holiday days</span>
                <span />
              </div>
              {form.tiers.map((tier, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[1fr_1fr_auto] items-center gap-3 border-b border-gray-100 px-4 py-3 last:border-0"
                >
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={tier.minimumQualifyingDays}
                    onChange={(event) =>
                      onTierChange(
                        index,
                        "minimumQualifyingDays",
                        event.target.value,
                      )
                    }
                    placeholder="6"
                    className={inputClass}
                  />
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={tier.holidayDays}
                    onChange={(event) =>
                      onTierChange(index, "holidayDays", event.target.value)
                    }
                    placeholder="1"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveTier(index)}
                    disabled={form.tiers.length === 1}
                    className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label="Remove tier"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              name="isActive"
              checked={form.isActive}
              onChange={onChange}
              className="h-4 w-4 accent-primary-700"
            />{" "}
            Make this policy active and available for employee assignment
          </label>
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-700 px-5 py-3 text-sm font-semibold text-white hover:bg-primary-800 disabled:opacity-60"
            >
              <FiCheckCircle />{" "}
              {isSaving
                ? "Saving…"
                : editingPolicy
                  ? "Save changes"
                  : "Create policy"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalField({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-gray-700">
        {label}
      </span>
      {children}
      {hint ? (
        <span className="mt-1 block text-xs text-gray-500">{hint}</span>
      ) : null}
    </label>
  );
}
